import { useNavigate } from "react-router";
import { Box, Button, Center, Text } from "@chakra-ui/react";
import { ShieldSVG, CheckSVG2, CloseSVG2, PendingSVG, ExpiredSVG } from "assets";
import { ActionBar, CircularLoader, CloseWindow, PageMotion, PoweredBy, WarningText } from "components";
import { useGetKycStatusQuery } from "apis";
import { Link } from "react-router-dom";
import { useSearchParams } from "hooks";

type DocumentStatus = "null" | "pending" | "approved" | "expired" | "rejected";

interface IStatus {
  status: DocumentStatus;
  description: string;
  bg: string;
}

const KycStatus = () => {
  const navigate = useNavigate();
  const { data, isSuccess } = useGetKycStatusQuery();

  const mapResult: Record<DocumentStatus, IStatus> = {
    null: {
      status: "null",
      description: "No KYC application",
      bg: "rgba(237, 28, 0, 0.2)",
    },

    pending: {
      status: "pending",
      description: "KYC Pending Approval",
      bg: " rgba(49, 183, 169, 0.2)",
    },

    approved: {
      status: "approved",
      description: "KYC Approved",
      bg: "#E2F2DA",
    },

    expired: {
      status: "expired",
      description: "KYC Expired",
      bg: "#CACBCB",
    },

    rejected: {
      status: "rejected",
      description: "KYC Rejected",
      bg: "#CACBCB",
    },
  };

  const result = mapResult[data?.status as DocumentStatus];

  const partnerType = useSearchParams("type");

  return (
    <PageMotion px={{ base: "8px", "1sm": "4px" }}>
      <Box maxW="435px" m="0 auto" pos="relative">
        <ActionBar onBackClick={() => navigate(-1)} title="KYC Status" />
      </Box>

      <Box minH="75vh" maxW="435px" mx="auto">
        {!isSuccess && (
          <Center mt="24px">
            <CircularLoader />
          </Center>
        )}

        {isSuccess && (
          <Box>
            <Center
              flexDir="column"
              gap="27px"
              borderBottom="0.5px solid #CACBCB"
              padding={{ base: "60px 0 40px", "1sm": "120px 0 40px" }}
            >
              <Center width="62px" height="62px" borderRadius="full" bg={result?.bg}>
                {result?.status === "approved" && <CheckSVG2 />}
                {result?.status === "pending" && <PendingSVG />}
                {result?.status === "expired" && <ExpiredSVG />}

                {result?.status === "null" && <CloseSVG2 />}
              </Center>

              <Text color="#303131" fontSize="16px" fontWeight="500" textAlign="center">
                {result?.description}
              </Text>
            </Center>

            <Center margin="34px 0 82px" gap="4px">
              <Center borderRadius="full" width="36px" height="36px" bg={result?.bg}>
                <ShieldSVG />
              </Center>

              <Text textTransform="capitalize" fontSize="14px" fontWeight="600" fontFamily="var(--bitmama-fonts-heading)">
                Status: {result?.status === "null" ? "Not Started" : result?.status}
              </Text>
            </Center>

            {result?.status === "pending" && (
              <WarningText>
                <Text mb="6px">We are verifying your KYC.</Text>
                <Text>
                  Anticipated completion time is{" "}
                  <Text as="span" fontWeight="600">
                    10 - 60 minutes
                  </Text>
                  . Feel free to close this page; we'll notify you via email about the verification status.
                </Text>
              </WarningText>
            )}

            <Center>
              {(result?.status === "null" || result?.status === "expired" || result?.status === "rejected") && (
                <Link to={partnerType ? `/kyc?type=partner` : "/kyc"}>
                  <Button minW={{ base: "100%", "1sm": "420px" }}>Start KYC Application</Button>
                </Link>
              )}
            </Center>
          </Box>
        )}
      </Box>

      {!partnerType && (
        <Center>
          <CloseWindow />
        </Center>
      )}

      <PoweredBy />
    </PageMotion>
  );
};

export default KycStatus;
